/**
 * Asset Layout Engine
 * Intelligent spatial placement system for VR training assets
 */

import {
    SPATIAL_ZONES,
    HEIGHT_RULES,
    COLLISION_PARAMS,
    VR_ERGONOMICS,
    getAssetHeight,
    categorizeAsset,
    detectLayoutPattern
} from '../constants/positioningConstants.js';

/**
 * Grid-based placement tracker to prevent overlaps
 */
class PlacementGrid {
    constructor(zone, gridSize = 0.3) {
        this.zone = zone;
        this.gridSize = gridSize; // 30cm grid cells
        this.occupiedCells = new Set();
    }

    /**
     * Convert world position to grid cell key
     */
    positionToCell(x, z) {
        const gridX = Math.floor(x / this.gridSize);
        const gridZ = Math.floor(z / this.gridSize);
        return `${gridX},${gridZ}`;
    }

    /**
     * Check if position is occupied
     */
    isOccupied(x, z, radius = 0.25) {
        // Check the cell and adjacent cells
        const cells = [];
        const cellRadius = Math.ceil(radius / this.gridSize);

        for (let dx = -cellRadius; dx <= cellRadius; dx++) {
            for (let dz = -cellRadius; dz <= cellRadius; dz++) {
                cells.push(this.positionToCell(x + dx * this.gridSize, z + dz * this.gridSize));
            }
        }

        return cells.some(cell => this.occupiedCells.has(cell));
    }

    /**
     * Mark position as occupied
     */
    occupy(x, z, radius = 0.25) {
        const cellRadius = Math.ceil(radius / this.gridSize);

        for (let dx = -cellRadius; dx <= cellRadius; dx++) {
            for (let dz = -cellRadius; dz <= cellRadius; dz++) {
                this.occupiedCells.add(this.positionToCell(x + dx * this.gridSize, z + dz * this.gridSize));
            }
        }
    }

    /**
     * Find next available slot in zone
     */
    findAvailableSlot(preferredX = null, preferredZ = null, radius = 0.25) {
        const center = this.zone.center;
        const searchRadius = this.zone.radius || Math.max(this.zone.dimensions?.width || 2, this.zone.dimensions?.depth || 2);

        // If preferred position provided, try it first
        if (preferredX !== null && preferredZ !== null) {
            if (!this.isOccupied(preferredX, preferredZ, radius)) {
                return { x: preferredX, z: preferredZ };
            }
        }

        // Spiral search pattern from center
        const maxSteps = 100;
        let angle = 0;
        let distance = this.gridSize;

        for (let i = 0; i < maxSteps; i++) {
            const x = center.x + Math.cos(angle) * distance;
            const z = center.z + Math.sin(angle) * distance;

            if (!this.isOccupied(x, z, radius) && distance <= searchRadius) {
                return { x, z };
            }

            angle += 0.5; // Rotate
            if (angle > Math.PI * 2) {
                angle = 0;
                distance += this.gridSize; // Expand spiral
            }
        }

        // Fallback: return center with offset
        return {
            x: center.x + (Math.random() - 0.5) * searchRadius,
            z: center.z + (Math.random() - 0.5) * searchRadius
        };
    }
}

/**
 * Main Asset Layout Engine
 */
export class AssetLayoutEngine {
    constructor(trainingContent = '', existingAssets = []) {
        this.trainingContent = trainingContent;
        this.existingAssets = existingAssets;

        // Detect training pattern
        this.layoutPattern = detectLayoutPattern(trainingContent);

        // Initialize placement grids for each zone
        this.grids = {};
        for (const [zoneName, zoneConfig] of Object.entries(SPATIAL_ZONES)) {
            this.grids[zoneName] = new PlacementGrid(zoneConfig);
        }

        // Mark existing assets as occupied
        this.initializeExistingAssets(existingAssets);

        // Counter for workflow ordering
        this.placementCounters = {
            workbench: 0,
            wall_left: 0,
            wall_right: 0,
            wall_back: 0,
            tool_rack: 0,
            safety_station: 0,
            inspection_area: 0,
            storage_shelf: 0
        };
    }

    /**
     * Mark existing assets as occupied in grids
     */
    initializeExistingAssets(assets) {
        for (const asset of assets) {
            if (asset.transform?.position) {
                const pos = asset.transform.position;
                const size = asset.transform?.scale || { x: 1, y: 1, z: 1 };
                const radius = Math.max(size.x, size.z) * 0.5;

                // Find closest zone and mark as occupied
                const zoneName = this.findClosestZone(pos);
                if (this.grids[zoneName]) {
                    this.grids[zoneName].occupy(pos.x, pos.z, radius);
                }
            }
        }
    }

    /**
     * Find closest zone to a position
     */
    findClosestZone(position) {
        let minDist = Infinity;
        let closestZone = 'workbench';

        for (const [zoneName, zoneConfig] of Object.entries(SPATIAL_ZONES)) {
            const center = zoneConfig.center;
            const dist = Math.sqrt(
                Math.pow(position.x - center.x, 2) +
                Math.pow(position.z - center.z, 2)
            );

            if (dist < minDist) {
                minDist = dist;
                closestZone = zoneName;
            }
        }

        return closestZone;
    }

    /**
     * Main method: Place an asset intelligently (position only)
     */
    placeAsset(asset, options = {}) {
        const { workflowOrder = null, proximityTo = [] } = options;

        // 1. Categorize the asset
        const category = categorizeAsset(asset.name, asset.description);

        // 2. Get appropriate height
        const heightInfo = getAssetHeight(asset.name, asset.description);

        // 3. Determine placement zone based on category and layout pattern
        const zoneName = this.selectZone(category, asset.mandatory);

        // 4. Calculate position within zone
        const position = this.calculatePosition(
            zoneName,
            category,
            asset,
            workflowOrder,
            proximityTo
        );

        // 5. Mark position as occupied
        const assetRadius = this.estimateAssetRadius(asset);
        if (this.grids[zoneName]) {
            this.grids[zoneName].occupy(position.x, position.z, assetRadius);
        }


        return {
            position: {
                x: parseFloat(position.x.toFixed(2)),
                y: parseFloat(heightInfo.y.toFixed(2)),
                z: parseFloat(position.z.toFixed(2))
            },
            metadata: {
                zone: zoneName,
                category: category.category,
                heightReason: heightInfo.reason,
                layoutPattern: this.layoutPattern.pattern
            }
        };
    }

    /**
     * Select appropriate zone for asset
     */
    selectZone(category, isMandatory = false) {
        const pattern = this.layoutPattern.config;

        // Map category to preferred zone
        const zoneMap = {
            wall_mounted: ['wall_back', 'wall_left', 'wall_right'],
            floor_equipment: ['floor_area'],
            handheld_tool: ['tool_rack', 'workbench'],
            safety_equipment: ['safety_station', 'storage_shelf'],
            workbench_item: ['workbench', 'inspection_area']
        };

        const preferredZones = zoneMap[category.zone] || ['workbench'];

        // Filter by available zones in current layout pattern
        const availableZones = preferredZones.filter(zone =>
            pattern.zones.includes(zone)
        );

        // Select zone with most space
        if (availableZones.length > 0) {
            // Find zone with least occupied space
            let bestZone = availableZones[0];
            let minOccupied = this.grids[bestZone]?.occupiedCells.size || 0;

            for (const zone of availableZones) {
                const occupied = this.grids[zone]?.occupiedCells.size || 0;
                if (occupied < minOccupied) {
                    minOccupied = occupied;
                    bestZone = zone;
                }
            }

            return bestZone;
        }

        // Fallback to first available zone in pattern
        return pattern.zones[0] || 'workbench';
    }

    /**
     * Calculate position within selected zone
     */
    calculatePosition(zoneName, category, asset, workflowOrder, proximityTo) {
        const zone = SPATIAL_ZONES[zoneName];
        const grid = this.grids[zoneName];
        const assetRadius = this.estimateAssetRadius(asset);

        // Handle different zone types
        if (zoneName.startsWith('wall_')) {
            return this.placeOnWall(zone, zoneName, workflowOrder);
        } else if (zoneName === 'workbench') {
            return this.placeOnWorkbench(zone, grid, workflowOrder, assetRadius);
        } else if (zoneName === 'tool_rack') {
            return this.placeInToolRack(zone, zoneName, assetRadius);
        } else if (zoneName === 'safety_station') {
            return this.placeInSafetyStation(zone, grid, assetRadius);
        } else if (zoneName === 'floor_area') {
            return this.placeOnFloor(zone, grid, workflowOrder, assetRadius);
        } else {
            // Default: find available slot
            return grid.findAvailableSlot(null, null, assetRadius);
        }
    }

    /**
     * Place asset on wall
     */
    placeOnWall(zone, zoneName, workflowOrder) {
        const counter = this.placementCounters[zoneName]++;
        const spacing = zone.spacing || 0.5;
        const verticalSpacing = zone.vertical_spacing || 0.4;

        // Arrange in grid pattern on wall
        const itemsPerRow = Math.ceil(Math.sqrt(zone.capacity || 8));
        const row = Math.floor(counter / itemsPerRow);
        const col = counter % itemsPerRow;

        return {
            x: zone.center.x + (col - itemsPerRow / 2) * spacing,
            z: zone.center.z // Fixed Z for wall
        };
    }

    /**
     * Place asset on workbench in workflow order
     */
    placeOnWorkbench(zone, grid, workflowOrder, radius) {
        const center = zone.center;
        const width = zone.dimensions.width;
        const depth = zone.dimensions.depth;

        if (workflowOrder !== null) {
            // Arrange left-to-right based on workflow
            const x = center.x - width / 2 + (workflowOrder % 4) * (width / 4);
            const z = center.z - depth / 2 + Math.floor(workflowOrder / 4) * (depth / 2);

            if (!grid.isOccupied(x, z, radius)) {
                return { x, z };
            }
        }

        // Find available slot
        return grid.findAvailableSlot(center.x, center.z, radius);
    }

    /**
     * Place tool in tool rack (vertical arrangement)
     */
    placeInToolRack(zone, zoneName, radius) {
        const counter = this.placementCounters[zoneName]++;
        const spacing = zone.spacing || 0.3;
        const verticalSpacing = zone.vertical_spacing || 0.3;

        // Arrange vertically then horizontally
        const itemsPerCol = 5;
        const col = Math.floor(counter / itemsPerCol);
        const row = counter % itemsPerCol;

        return {
            x: zone.center.x + col * spacing,
            z: zone.center.z
        };
    }

    /**
     * Place in safety station (radial arrangement)
     */
    placeInSafetyStation(zone, grid, radius) {
        const center = zone.center;
        const stationRadius = zone.radius || 0.8;
        const counter = this.placementCounters.safety_station++;

        // Arrange in circle around center
        const angle = (counter * 2 * Math.PI) / (zone.capacity || 6);
        const x = center.x + Math.cos(angle) * stationRadius;
        const z = center.z + Math.sin(angle) * stationRadius;

        return { x, z };
    }

    /**
     * Place large equipment on floor
     */
    placeOnFloor(zone, grid, workflowOrder, radius) {
        const center = zone.center;
        const maxRadius = zone.radius || 5;

        if (workflowOrder !== null) {
            // Arrange in line for workflow
            const x = center.x + (workflowOrder - 2) * 1.5; // 1.5m spacing
            const z = center.z;

            if (!grid.isOccupied(x, z, radius)) {
                return { x, z };
            }
        }

        return grid.findAvailableSlot(center.x, center.z, radius);
    }

    /**
     * Calculate rotation based on zone
     */
    calculateRotation(zoneName, category) {
        const rotation = { x: 0, y: 0, z: 0 };

        // Wall-mounted items face outward
        if (zoneName === 'wall_back') {
            rotation.y = 0; // Face forward
        } else if (zoneName === 'wall_left') {
            rotation.y = 90; // Face right
        } else if (zoneName === 'wall_right') {
            rotation.y = -90; // Face left
        }

        // Control panels slightly angled down
        if (category.category === 'control_panel') {
            rotation.x = -15;
        }

        return rotation;
    }

    /**
     * Estimate asset radius for collision detection
     */
    estimateAssetRadius(asset) {
        // Use scale if available
        if (asset.transform?.scale) {
            const scale = asset.transform.scale;
            return Math.max(scale.x, scale.z) * 0.5;
        }

        // Use expected size if available
        if (asset.expectedSize) {
            return asset.expectedSize / 200; // Convert cm to meters and radius
        }

        // Default
        return COLLISION_PARAMS.default_object_radius;
    }

    /**
     * Batch place multiple assets
     */
    placeAssets(assets) {
        const placedAssets = [];

        // Sort assets: mandatory first, then by workflow order hints
        const sortedAssets = [...assets].sort((a, b) => {
            if (a.mandatory && !b.mandatory) return -1;
            if (!a.mandatory && b.mandatory) return 1;
            return 0;
        });

        for (let i = 0; i < sortedAssets.length; i++) {
            const asset = sortedAssets[i];
            const transform = this.placeAsset(asset, {
                workflowOrder: i,
                proximityTo: asset.proximity_to || []
            });

            placedAssets.push({
                ...asset,
                transform: {
                    ...transform,
                    scale: asset.transform?.scale || { x: 1, y: 1, z: 1 }
                }
            });
        }

        return placedAssets;
    }

    /**
     * Validate placement (check for issues)
     */
    validatePlacement(assets) {
        const issues = [];

        for (let i = 0; i < assets.length; i++) {
            const asset = assets[i];
            const pos = asset.transform.position;

            // Check for collisions with other assets
            for (let j = i + 1; j < assets.length; j++) {
                const other = assets[j];
                const otherPos = other.transform.position;

                const distance = Math.sqrt(
                    Math.pow(pos.x - otherPos.x, 2) +
                    Math.pow(pos.z - otherPos.z, 2)
                );

                const minDist = COLLISION_PARAMS.min_spacing;

                if (distance < minDist) {
                    issues.push({
                        type: 'overlap',
                        assets: [asset.name, other.name],
                        distance,
                        severity: 'high'
                    });
                }
            }

            // Check reachability
            const distanceFromCenter = Math.sqrt(pos.x * pos.x + pos.z * pos.z);
            if (distanceFromCenter > VR_ERGONOMICS.MAX_REACH * 5) {
                issues.push({
                    type: 'too_far',
                    asset: asset.name,
                    distance: distanceFromCenter,
                    severity: 'medium'
                });
            }
        }

        return {
            valid: issues.length === 0,
            issues
        };
    }
}

export default AssetLayoutEngine;
